import { JsonObject } from '../../app/json/jsonValue.js';
import {
    PreprocessedTemplate, TemplatePlaceholder, SlottedTemplate, SlotPlaceholder,
    JsonKeyPlaceholder, JsonArrayPlaceholder, JsonArraySkipPlaceholder, ReplacementMapping, ReplacementType
} from '../../model/index.js';

/**
 * Purpose:
 * Rehydrates a PreprocessedTemplate from its serialized artifact form — the exact inverse of
 * SerializePreProcessMetadata.serializeTemplate (the canonical Preprocessed artifact shape stored in
 * api.json and the OCI ".ociartifacts/preprocessed/" layer).
 * Mirrors csharp Assembler/PreProcess/Loader/DeserializePreProcessMetadata.cs.
 *
 * Responsibilities:
 * - Decode the complete capitalized preprocessed shape (placeholders, slots, json placeholders,
 *   replacement mappings) so PreProcess client/OCI mode can rehydrate the server-computed structure
 *   instead of re-parsing raw HTML (which would make PreProcess degenerate into Normal).
 * - Stay byte-shape-aligned with the serializer; arrays absent in the JSON default to empty lists.
 */
export class DeserializePreProcessMetadata {
    /** Decodes one serialized preprocessed-template object into a PreprocessedTemplate. */
    static deserialize(json: JsonObject): PreprocessedTemplate {
        const t = new PreprocessedTemplate();
        t.originalContent = DeserializePreProcessMetadata.str(json, 'OriginalContent');
        const atn = json.tryGetValue('ActualTemplateName');
        t.actualTemplateName = atn.found && atn.value.isString ? atn.value.getString() : null;

        for (const o of DeserializePreProcessMetadata.objects(json, 'Placeholders'))
            t.placeholders.push(DeserializePreProcessMetadata.readPlaceholder(o));
        for (const o of DeserializePreProcessMetadata.objects(json, 'SlottedTemplates'))
            t.slottedTemplates.push(DeserializePreProcessMetadata.readSlottedTemplate(o));
        for (const o of DeserializePreProcessMetadata.objects(json, 'JsonKeyPlaceholders'))
            t.jsonKeyPlaceholders.push(DeserializePreProcessMetadata.readJsonKey(o));
        for (const o of DeserializePreProcessMetadata.objects(json, 'JsonSkipPlaceholders'))
            t.jsonSkipPlaceholders.push(DeserializePreProcessMetadata.readJsonKey(o));
        for (const o of DeserializePreProcessMetadata.objects(json, 'JsonArrayPlaceholders'))
            t.jsonArrayPlaceholders.push(DeserializePreProcessMetadata.readJsonArray(o));
        for (const o of DeserializePreProcessMetadata.objects(json, 'JsonArraySkipPlaceholders'))
            t.jsonArraySkipPlaceholders.push(DeserializePreProcessMetadata.readJsonArraySkip(o));
        for (const o of DeserializePreProcessMetadata.objects(json, 'ReplacementMappings'))
            t.replacementMappings.push(DeserializePreProcessMetadata.readReplacementMapping(o));

        return t;
    }

    private static readPlaceholder(json: JsonObject): TemplatePlaceholder {
        const p = new TemplatePlaceholder();
        p.name = DeserializePreProcessMetadata.str(json, 'Name');
        p.startIndex = DeserializePreProcessMetadata.int(json, 'StartIndex');
        p.endIndex = DeserializePreProcessMetadata.int(json, 'EndIndex');
        p.fullMatch = DeserializePreProcessMetadata.str(json, 'FullMatch');
        p.templateKey = DeserializePreProcessMetadata.str(json, 'TemplateKey');
        for (const o of DeserializePreProcessMetadata.objects(json, 'NestedPlaceholders'))
            p.nestedPlaceholders.push(DeserializePreProcessMetadata.readPlaceholder(o));
        for (const o of DeserializePreProcessMetadata.objects(json, 'NestedSlots'))
            p.nestedSlots.push(DeserializePreProcessMetadata.readSlot(o));
        return p;
    }

    private static readSlottedTemplate(json: JsonObject): SlottedTemplate {
        const s = new SlottedTemplate();
        s.name = DeserializePreProcessMetadata.str(json, 'Name');
        s.startIndex = DeserializePreProcessMetadata.int(json, 'StartIndex');
        s.endIndex = DeserializePreProcessMetadata.int(json, 'EndIndex');
        s.fullMatch = DeserializePreProcessMetadata.str(json, 'FullMatch');
        s.innerContent = DeserializePreProcessMetadata.str(json, 'InnerContent');
        s.templateKey = DeserializePreProcessMetadata.str(json, 'TemplateKey');
        for (const o of DeserializePreProcessMetadata.objects(json, 'Slots'))
            s.slots.push(DeserializePreProcessMetadata.readSlot(o));
        return s;
    }

    private static readSlot(json: JsonObject): SlotPlaceholder {
        const s = new SlotPlaceholder();
        s.number = DeserializePreProcessMetadata.str(json, 'Number');
        s.startIndex = DeserializePreProcessMetadata.int(json, 'StartIndex');
        s.endIndex = DeserializePreProcessMetadata.int(json, 'EndIndex');
        s.content = DeserializePreProcessMetadata.str(json, 'Content');
        s.slotKey = DeserializePreProcessMetadata.str(json, 'SlotKey');
        s.openTag = DeserializePreProcessMetadata.str(json, 'OpenTag');
        s.closeTag = DeserializePreProcessMetadata.str(json, 'CloseTag');
        s.hasNestedPlaceholders = DeserializePreProcessMetadata.bool(json, 'HasNestedPlaceholders');
        s.hasNestedSlottedTemplates = DeserializePreProcessMetadata.bool(json, 'HasNestedSlottedTemplates');
        s.requiresNestedProcessing = DeserializePreProcessMetadata.bool(json, 'RequiresNestedProcessing');
        for (const o of DeserializePreProcessMetadata.objects(json, 'NestedPlaceholders'))
            s.nestedPlaceholders.push(DeserializePreProcessMetadata.readPlaceholder(o));
        for (const o of DeserializePreProcessMetadata.objects(json, 'NestedSlottedTemplates'))
            s.nestedSlottedTemplates.push(DeserializePreProcessMetadata.readSlottedTemplate(o));
        for (const o of DeserializePreProcessMetadata.objects(json, 'NestedSlots'))
            s.nestedSlots.push(DeserializePreProcessMetadata.readSlot(o));
        return s;
    }

    private static readJsonKey(json: JsonObject): JsonKeyPlaceholder {
        const k = new JsonKeyPlaceholder();
        k.keyName = DeserializePreProcessMetadata.str(json, 'KeyName');
        k.startIndex = DeserializePreProcessMetadata.int(json, 'StartIndex');
        k.endIndex = DeserializePreProcessMetadata.int(json, 'EndIndex');
        k.fullMatch = DeserializePreProcessMetadata.str(json, 'FullMatch');
        return k;
    }

    private static readJsonArray(json: JsonObject): JsonArrayPlaceholder {
        const a = new JsonArrayPlaceholder();
        a.arrayName = DeserializePreProcessMetadata.str(json, 'ArrayName');
        a.startIndex = DeserializePreProcessMetadata.int(json, 'StartIndex');
        a.endIndex = DeserializePreProcessMetadata.int(json, 'EndIndex');
        a.fullMatch = DeserializePreProcessMetadata.str(json, 'FullMatch');
        a.blockContent = DeserializePreProcessMetadata.str(json, 'BlockContent');
        a.negativeBlockContent = DeserializePreProcessMetadata.str(json, 'NegativeBlockContent');
        a.fullMatchWithNegative = DeserializePreProcessMetadata.str(json, 'FullMatchWithNegative');
        a.hasNegativeBlock = DeserializePreProcessMetadata.bool(json, 'HasNegativeBlock');
        a.isPairedNegative = DeserializePreProcessMetadata.bool(json, 'IsPairedNegative');
        a.nestedKeys = DeserializePreProcessMetadata.strings(json, 'NestedKeys');
        // NestedArrays carries the conditional/nested array key NAMES (mirrors csharp List<string>).
        a.nestedArrays = DeserializePreProcessMetadata.strings(json, 'NestedArrays');
        return a;
    }

    private static readJsonArraySkip(json: JsonObject): JsonArraySkipPlaceholder {
        const a = new JsonArraySkipPlaceholder();
        a.arrayName = DeserializePreProcessMetadata.str(json, 'ArrayName');
        a.startIndex = DeserializePreProcessMetadata.int(json, 'StartIndex');
        a.endIndex = DeserializePreProcessMetadata.int(json, 'EndIndex');
        a.fullMatch = DeserializePreProcessMetadata.str(json, 'FullMatch');
        return a;
    }

    private static readReplacementMapping(json: JsonObject): ReplacementMapping {
        const m = new ReplacementMapping();
        m.startIndex = DeserializePreProcessMetadata.int(json, 'StartIndex');
        m.endIndex = DeserializePreProcessMetadata.int(json, 'EndIndex');
        m.originalText = DeserializePreProcessMetadata.str(json, 'OriginalText');
        m.replacementText = DeserializePreProcessMetadata.str(json, 'ReplacementText');
        const tt = json.tryGetValue('TargetTemplateName');
        m.targetTemplateName = tt.found && tt.value.isString ? tt.value.getString() : null;
        const ty = json.tryGetValue('Type');
        if (ty.found && ty.value.isString) {
            const parsed = ty.value.getString();
            if (parsed === ReplacementType.SimpleTemplate || parsed === ReplacementType.SlottedTemplate) {
                m.type = parsed;
            }
        }
        return m;
    }

    // ---- JSON read helpers (mirror DeserializeAppSiteMetadata idiom) ----

    private static str(json: JsonObject, key: string): string {
        const v = json.tryGetValue(key);
        return v.found && v.value.isString ? v.value.getString() : '';
    }

    private static int(json: JsonObject, key: string): number {
        const v = json.tryGetValue(key);
        if (!v.found) return 0;
        if (v.value.isInteger) return v.value.getInteger();
        if (v.value.isNumber) return Math.trunc(v.value.getNumber());
        return 0;
    }

    private static bool(json: JsonObject, key: string): boolean {
        const v = json.tryGetValue(key);
        return v.found && v.value.isBool && v.value.getBool();
    }

    private static strings(json: JsonObject, key: string): string[] {
        const result: string[] = [];
        const v = json.tryGetValue(key);
        if (v.found && v.value.isArray) {
            for (const item of v.value.getArray()) {
                if (item.isString) result.push(item.getString());
            }
        }
        return result;
    }

    private static *objects(json: JsonObject, key: string): IterableIterator<JsonObject> {
        const v = json.tryGetValue(key);
        if (v.found && v.value.isArray) {
            for (const item of v.value.getArray()) {
                if (item.isObject) yield item.getObject();
            }
        }
    }
}
